import { Navigate, RouteObject, useLocation } from 'react-router-dom';
import i18next from 'i18n';
import { useEffect, useState } from 'react';
import Layout from 'components/layout/Layout';
import NotFound from 'components/notFound/NotFound';
import {ProtectedRoute, Auth2ProtectedRoute} from 'components/protectedRoute/ProtectedRoute';
import { useSession } from 'context/SessionContext';
import { TokenUpgradeResponse } from 'types/dto/login';
import NewOrder from 'pages/order/newOrder/NewOrder';
import OrderHistoryList from 'pages/order/orderHistoryList/OrderHistoryList';
import OrderHistoryDetail from 'pages/order/orderHistoryDetail/OrderHistoryDetail';
import Profile from 'pages/profile/Profile';
import Redeem from 'pages/redeem/Redeem';
import Tips from 'pages/tips/Tips';
import CouponsAndVouchers from 'pages/wallet/couponsAndVouchers/CouponsAndVouchers';
import Benefits from 'pages/benefits/Benefits';
import Landing from 'pages/landing/Landing';
import NewOrderResult from 'pages/order/newOrderResult/NewOrderResult';
import Login from 'pages/login/Login';
import Registration from 'pages/registration/registration/Registration';
import RegistrationSuccess from 'pages/registration/registrationSuccess/RegistrationSuccess';
import PasswordCreateOrReset from 'pages/password/passwordCreateOrReset/PasswordCreateOrReset';
import PasswordInput from 'pages/password/passwordInput/PasswordInput';
import PasswordUpdateSuccess from 'pages/password/passwordUpdateSuccess/PasswordUpdateSuccess';
import PasswordResetEmailSent from 'pages/password/passwordResetEmailSent/PasswordResetEmailSent';
import EmailInput from 'pages/email/emailInput/EmailInput';
import EmailSent from 'pages/email/emailSent/EmailSent';
import EmailInputEmailVerified from 'pages/email/emailInputEmailVerified/EmailInputEmailVerified';
import EmailLinkExpired from 'pages/email/emailLinkExpired/EmailLinkExpired';
import EmailLinkExpiredWithSignUp from 'pages/email/emailLinkExpiredWithSignUp/EmailLinkExpiredWithSignUp';
import SessionExpired from 'pages/sessionExpired/SessionExpired';
import EmailVerify from 'pages/email/emailVerify/EmailVerify';
import PasswordResetEmailVerify from 'pages/password/passwordResetEmailVerify/PasswordResetEmailVerify';
import PolicyPage from 'pages/policy/policy';
import { checksession } from 'services/loginService';
import { normalizeLang } from 'utils/commonUtils';

const AuthRedirect = ({ children }: { children?: React.ReactNode }) => {
  const { session } = useSession();
  const lang = normalizeLang(i18next.language || 'en');
  const [isValid, setIsValid] = useState<TokenUpgradeResponse | null>(null);
  useEffect(() => {
    const validate = async () => {
      if (session && session._at === 'password' && session._tk) {
        const response = await checksession();
        setIsValid(response);
      }
    };
    validate();
  }, [session]);
  if (session && session._at === 'password' && isValid) {
    return isValid
      ? <Navigate to={`/${lang}/landing`} replace />
      : <Navigate to={`/${lang}/signin`} replace />;
  }
  return children ? <>{children}</> : <Navigate to={`/${lang}/signin`} replace />;
};

// Redirects lang-less deep links (e.g. from emails) to the lang-prefixed equivalent,
// preserving all query parameters.
const LanglessRedirect = ({ to }: { to: string }) => {
  const lang = normalizeLang(i18next.language || 'en');
  const { search } = useLocation();
  return <Navigate to={`/${lang}/${to}${search}`} replace />;
};

export const routes: RouteObject[] = [
  {
    path: '/:lang',
    element: <Layout></Layout>,
    children: [
      // Protected Routes (sorted alphabetically)
      {
        path: '',
        element: <ProtectedRoute><Login /></ProtectedRoute>,
      },
       {
         path: 'benefits',
         element: <Auth2ProtectedRoute><Benefits /></Auth2ProtectedRoute>,
       },
      {
        path: 'email-input',
        element: <ProtectedRoute><EmailInput/></ProtectedRoute>,
      },
      {
        path: 'email-input-email-verified',
        element: <ProtectedRoute><EmailInputEmailVerified/></ProtectedRoute>,
      },
      {
        path: 'email-sent',
        element: <ProtectedRoute><EmailSent/></ProtectedRoute>,
      },
      {
        path: 'landing',
        element: <Auth2ProtectedRoute><Landing /></Auth2ProtectedRoute>,
      },
      {
        path: 'order/create-order',
        element: <Auth2ProtectedRoute><NewOrder /></Auth2ProtectedRoute>,
      },
      {
        path: 'order/order-history',
        element: <Auth2ProtectedRoute><OrderHistoryList /></Auth2ProtectedRoute>,
      },
      {
        path: 'order/order-history/:id',
        element: <Auth2ProtectedRoute><OrderHistoryDetail /></Auth2ProtectedRoute>,
      },
      {
        path: 'order/order-result',
        element: <Auth2ProtectedRoute><NewOrderResult /></Auth2ProtectedRoute>,
      },
      {
        path: 'password-create',
        element: <ProtectedRoute><PasswordCreateOrReset type='create'/></ProtectedRoute>,
      },
      {
        path: 'password-input',
        element: <ProtectedRoute><PasswordInput type='login'/></ProtectedRoute>,
      },
      {
        path: 'password-reset',
        element: <ProtectedRoute><PasswordCreateOrReset type='reset'/></ProtectedRoute>,
      },
      {
        path: 'password-reset-email-sent',
        element: <ProtectedRoute><PasswordResetEmailSent/></ProtectedRoute>,
      },
      {
        path: 'profile',
        element: <Auth2ProtectedRoute><Profile /></Auth2ProtectedRoute>,
      },
      {
        path: 'redeem',
        element: <Auth2ProtectedRoute><Redeem /></Auth2ProtectedRoute>,
      },
      {
        path: 'registration',
        element: <ProtectedRoute><Registration/></ProtectedRoute>,
      },
      {
        path: 'tips',
        element: <Auth2ProtectedRoute><Tips /></Auth2ProtectedRoute>,
      },
      {
        path: 'wallet/coupons-and-vouchers',
        element: <Auth2ProtectedRoute><CouponsAndVouchers /></Auth2ProtectedRoute>,
      },
      {
        path: 'wallet/expired-and-used',
        element: <Auth2ProtectedRoute><CouponsAndVouchers isExpiredOrUsed={true} /></Auth2ProtectedRoute>,
      },

      // Unprotected Routes (sorted alphabetically)
      {
        path: 'email-verify',
        element: <EmailVerify/>,
      },
      {
        path: 'password-reset-email-verify',
        element: <PasswordResetEmailVerify/>,
      },
      {
        path: 'email-link-expired',
        element: <EmailLinkExpired/>,
      },
      {
        path: 'email-link-expired-with-sign-up',
        element: <EmailLinkExpiredWithSignUp/>,
      },
      {
        path: 'signin',
        element: <AuthRedirect><Login /></AuthRedirect>,
      },
      {
        path: 'session-expired',
        element: <SessionExpired/>,
      },
      {
        path: 'registration-success',
        element: <RegistrationSuccess/>,
      },
      {
        path: 'password-create-success',
        element: <PasswordUpdateSuccess type='create'/>,
      },
      {
        path: 'password-reset-success',
        element: <PasswordUpdateSuccess type='reset'/>,
      },
      {
        path: 'password-change-success',
        element: <PasswordUpdateSuccess type='change'/>,
      },
      {
        path: 'policy/:slug',
        element: <PolicyPage />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },

  {
    path: '/',
    element: <AuthRedirect />,
  },
  // Lang-less deep links from emails — redirect to /:lang/... preserving query params
  {
    path: '/email-verify',
    element: <LanglessRedirect to="email-verify" />,
  },
  {
    path: '/password-reset-email-verify',
    element: <LanglessRedirect to="password-reset-email-verify" />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];